import React, { useEffect, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { RootState } from '../../types';
import { ContextualPrompt } from '../../utils/LeoUtils';
import NeoLeoChatInput from './Chat/NeoChatInput';
import NeoLeoChatPrompts from './NeoLeoChatPrompts';

interface NeoLeoChatFooterProps {
  isIntro?: boolean;
}

export interface ChatFormData {
  askLeoText?: string;
}

const NeoLeoChatFooter: React.FC<NeoLeoChatFooterProps> = ({
  isIntro = false,
}) => {
  const { msgContext, selectedItems, sessionId } = useSelector(
    (state: RootState) => ({
      msgContext: state.leo.msgContext,
      selectedItems: state.leo.selectedItems,
      sessionId: state.leo.session?.id,
    }),
  );

  const methods = useForm<ChatFormData>({
    defaultValues: {
      askLeoText: '',
    },
  });
  const { watch } = methods;
  const [showPrompts, setShowPrompts] = useState<boolean>(true);
  const [promptSuggestions, setPromptSuggestions] =
    useState<ContextualPrompt[]>();
  const askLeoText = watch('askLeoText');

  useEffect(() => {
    const prompts = msgContext?.details?.[0]?.prompts;
    const singleSelectionPrompts =
      !!prompts && !!prompts?.single_selection
        ? prompts?.single_selection?.filter((el) => !!el)
        : [];
    const multiSelectionPrompts =
      !!prompts && !!prompts?.multi_selection
        ? prompts?.multi_selection?.filter((el) => !!el)!
        : [];
    const responseContext = msgContext?.details?.[0]?.type || '';
    if (selectedItems.length > 1 && multiSelectionPrompts.length) {
      setPromptSuggestions(
        multiSelectionPrompts.map((p) => ({
          prompt: p!,
          category: responseContext,
          title: p!,
        })),
      );
      setShowPrompts(true);
      return;
    }
    if (selectedItems.length === 1 && singleSelectionPrompts.length) {
      setPromptSuggestions(
        singleSelectionPrompts.map((p) => ({
          prompt: p!,
          category: responseContext,
          title: p!,
        })),
      );
      setShowPrompts(true);
      return;
    }
    setPromptSuggestions(undefined);
  }, [msgContext, selectedItems.length]);

  useEffect(() => {
    setShowPrompts(true);
  }, [sessionId]);

  return (
    <FormProvider {...methods}>
      <div className='bg-reskin-regent-900 z-[48] mx-2 mb-2 px-3 pb-3 rounded-2xl border border-[#2D355C] relative pt-1'>
        {showPrompts && !askLeoText && (
          <div className='pt-2.5'>
            <p className='uppercase rounded-full px-2 bg-gradient-to-r from-reskin-aqua to-reskin-primary-blue absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-inter font-bold text-center'>
              leo&apos;s Suggestions
            </p>
            <NeoLeoChatPrompts
              onSelect={() => setShowPrompts(false)}
              promptSuggestions={promptSuggestions}
              contextType={msgContext?.details?.[0]?.type}
            />
          </div>
        )}
        <NeoLeoChatInput
          isIntro={isIntro}
          askLeoText={askLeoText}
          showPrompts={showPrompts}
          setShowPrompts={setShowPrompts}
        />
      </div>
    </FormProvider>
  );
};

export default NeoLeoChatFooter;
